import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, FileText, AlertCircle, CheckCircle, Loader2, X } from 'lucide-react';
import { apiService, FileCategory } from '../services/api';

interface DocumentFormData {
  strDisplayName: string;
  strCategoryGUID: string;
}

interface DocumentUploadProps {
  onUploadSuccess?: () => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ onUploadSuccess }) => {
  const [categories, setCategories] = useState<FileCategory[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<DocumentFormData>({
    strDisplayName: '',
    strCategoryGUID: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await apiService.getAllCategories();
        setCategories(data);
      } catch (error) {
        // Error loading categories
        setError('Failed to load categories');
      }
    };

    loadCategories();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile || !formData.strCategoryGUID || !formData.strDisplayName.trim()) {
      setError('Please select a file, category, and enter a display name');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const newDocument = await apiService.uploadDownload(selectedFile, formData.strCategoryGUID, formData.strDisplayName);
      setFormData({ strDisplayName: '', strCategoryGUID: '' });
      setSelectedFile(null);
      setSuccess('Document uploaded successfully');
      
      // Notify parent component about successful upload
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      // Error uploading document
      setError('Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ strDisplayName: '', strCategoryGUID: '' });
    setSelectedFile(null);
    setError(null);
    setSuccess(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <Upload className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Upload Documents</h2>
            <p className="text-gray-600">
              Upload documents to the selected category for easy access and management.
            </p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200 rounded-xl shadow-lg">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-green-800 font-medium">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-xl shadow-lg">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-red-800 font-medium">{error}</span>
          </div>
        </div>
      )}

      <Card className="border-2 border-blue-100 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <Upload className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold text-gray-900">Upload New Document</CardTitle>
              <CardDescription className="text-gray-600">
                Select a file, choose a category, and provide a display name for the document.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleUpload} className="space-y-6">
            {/* File Upload */}
            <div>
              <Label htmlFor="file" className="text-sm font-medium text-gray-700 mb-2 block">
                File *
              </Label>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-all duration-200">
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <Input
                  id="file"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                  className="hidden"
                  required
                />
                <label htmlFor="file" className="cursor-pointer">
                  <span className="text-sm font-medium text-blue-600 hover:text-blue-700">
                    Choose a file
                  </span>
                  <span className="text-xs text-gray-500 block mt-1">
                    or drag and drop
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Supported formats: PDF, DOC, DOCX, XLS, XLSX, TXT
                </p>
              </div>
              {selectedFile && (
                <div className="flex items-center gap-2 text-sm text-green-600 mt-3 p-3 bg-green-50 rounded-lg">
                  <FileText className="h-4 w-4" />
                  {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
            </div>

            {/* Display Name */}
            <div>
              <Label htmlFor="displayName" className="text-sm font-medium text-gray-700 mb-2 block">
                Display Name *
              </Label>
              <Input
                id="displayName"
                value={formData.strDisplayName}
                onChange={(e) => setFormData({ ...formData, strDisplayName: e.target.value })}
                placeholder="Enter a descriptive name for the document"
                className="h-12 border-2 border-gray-200 focus:border-blue-400 rounded-xl transition-all duration-200"
                required
              />
            </div>

            {/* Category Selection */}
            <div>
              <Label htmlFor="category" className="text-sm font-medium text-gray-700 mb-2 block">
                Category *
              </Label>
              <select
                id="category"
                value={formData.strCategoryGUID}
                onChange={(e) => setFormData({ ...formData, strCategoryGUID: e.target.value })}
                className="block w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-400 rounded-xl shadow-sm focus:outline-none bg-white text-gray-900 transition-all duration-200"
                required
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.strGUID} value={category.strGUID}>
                    {category.strName}
                  </option>
                ))}
              </select>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200 px-6"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={resetForm}
                className="border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 px-6"
              >
                <X className="h-4 w-4 mr-2" />
                Reset
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentUpload; 